type SessionApiResponse<T> = {
    data?: T;
    error?: unknown;
    response?: { status?: number };
};

type SessionCheckResult<T> =
    { status: 'authenticated'; data: T | undefined } | { status: 'unauthenticated' } | { status: 'unavailable' };

type Attempt<T> = {
    value: SessionApiResponse<T> | unknown;
    status?: number;
    succeeded: boolean;
};

const RETRY_DELAY_MS = 250;
export const SESSION_RECHECK_INTERVAL_MS = 10 * 60 * 1000;

export const shouldRecheckSession = (
    lastCheckedAt: number,
    now: number = Date.now(),
    visibilityState: DocumentVisibilityState = document.visibilityState
): boolean => visibilityState === 'visible' && now - lastCheckedAt >= SESSION_RECHECK_INTERVAL_MS;

const responseStatus = (value: unknown): number | undefined => {
    if (!value || typeof value !== 'object') return undefined;

    const directStatus = (value as { status?: unknown }).status;
    if (typeof directStatus === 'number') return directStatus;

    const response = (value as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return undefined;

    const status = (response as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
};

const runAttempt = async <T>(request: () => Promise<SessionApiResponse<T>>): Promise<Attempt<T>> => {
    try {
        const value = await request();
        return {
            value,
            status: responseStatus(value),
            succeeded: value.error === undefined || value.error === null,
        };
    } catch (value) {
        return { value, status: responseStatus(value), succeeded: false };
    }
};

const wait = (milliseconds: number) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

export const checkSessionWithRetry = async <T>(
    request: () => Promise<SessionApiResponse<T>>,
    delay: (milliseconds: number) => Promise<void> = wait
): Promise<SessionCheckResult<T>> => {
    const first = await runAttempt(request);
    if (first.succeeded) {
        return { status: 'authenticated', data: (first.value as SessionApiResponse<T>).data };
    }
    if (first.status !== 401) {
        return { status: 'unavailable' };
    }

    await delay(RETRY_DELAY_MS);
    const second = await runAttempt(request);
    if (second.succeeded) {
        return { status: 'authenticated', data: (second.value as SessionApiResponse<T>).data };
    }
    return second.status === 401 ? { status: 'unauthenticated' } : { status: 'unavailable' };
};
